"use client";

import { Minus, Plus } from "lucide-react";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../design-system/tooltip";
import type {
	KeyboardEvent,
	MouseEvent,
	ReactNode,
} from "react";
import { cn } from "../../lib";
import {
	SQ_AMBER_PAIR,
	SQ_GREY_FILL_CLASS,
	SQ_PRIMARY_CTA_GRADIENT_CLASS,
} from "../../lib/sq-color-runtime-exports";
import { Input } from "../input";
import { RippleButton } from "../material-ui/ripple-button";
/** Outer tile (`rounded-2xl` = 1rem). */
const TILE_RADIUS = "rounded-2xl";
/**
 * Inner corners inside `p-0.5` padding: 1rem − 0.125rem so the curve stays
 * concentric with the outer tile (2px inset per edge → 4px diameter step).
 */
const SPINNER_INNER_L = "rounded-l-[calc(1rem-0.125rem)] rounded-r-none";
const SPINNER_INNER_R = "rounded-r-[calc(1rem-0.125rem)] rounded-l-none";

const HIGHLIGHT_BORDER_PX = 2;
const DEFAULT_BADGE_LABEL = "MASTERED";

export type QuantitySpinnerColorMode = "system" | "light" | "dark";
export type QuantitySpinnerCenterMode = "display" | "input";
/** Edge ± chrome — matches PrimaryCta / SecondaryCta surfaces. */
export type QuantitySpinnerButtonVariant = "primary" | "secondary";
/** `compact` = shorter track + smaller ± buttons (e.g. Shop drawer footer). */
export type QuantitySpinnerSize = "default" | "compact";

export interface QuantitySpinnerProps {
	decreaseAriaLabel: string;
	increaseAriaLabel: string;
	onDecrease: () => void;
	onIncrease: () => void;
	decreaseDisabled?: boolean;
	increaseDisabled?: boolean;
	/** Shown when + is disabled (wraps trigger so disabled buttons still tooltip). */
	increaseDisabledTooltip?: string | null;
	/** Track + center chrome. Overlay uses `dark`; pricing uses `system`. */
	colorMode?: QuantitySpinnerColorMode;
	/** `display` = custom children; `input` = editable number field. */
	centerMode?: QuantitySpinnerCenterMode;
	/** ± button surface. Default `primary` (amber CTA). Pricing uses `secondary`. */
	buttonVariant?: QuantitySpinnerButtonVariant;
	/** Overall control height. Default matches pricing / completion overlays. */
	size?: QuantitySpinnerSize;
	/**
	 * Amber gradient border. When true and `badge` is omitted, shows
	 * {@link DEFAULT_BADGE_LABEL} inline left of the value (input mode).
	 */
	highlight?: boolean;
	/**
	 * Badge label when `highlight` is on. Pass `false` to hide.
	 * Rendered inline, left of and vertically aligned with the quantity text.
	 */
	badge?: string | false;
	className?: string;
	centerClassName?: string;
	/** Center content for `centerMode="display"`. */
	children?: ReactNode;
	/** Input props for `centerMode="input"`. */
	inputId?: string;
	inputValue?: string;
	onInputChange?: (value: string) => void;
	onInputFocus?: () => void;
	onInputBlur?: () => void;
	onInputKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
	onInputMouseDown?: (event: MouseEvent<HTMLInputElement>) => void;
	inputReadOnly?: boolean;
	inputDisabled?: boolean;
	inputAriaValueMin?: number;
	inputAriaValueMax?: number;
	inputAriaValueNow?: number;
	inputAriaValueText?: string;
	inputAriaInvalid?: boolean;
	inputAriaDescribedBy?: string;
	inputClassName?: string;
	/**
	 * Optional second line under the input (e.g. "per employee per week").
	 * Hidden while the field is focused/editing by the consumer omitting it.
	 */
	inputSubtitle?: string;
}

function trackSurfaceClass(colorMode: QuantitySpinnerColorMode): string {
	switch (colorMode) {
		case "light":
			return "bg-white";
		case "dark":
			return SQ_GREY_FILL_CLASS;
		case "system":
			// Explicit pair so Tailwind emits light + dark (SQ_GREY_FILL is solid-only).
			return "bg-white dark:bg-[var(--sq-grey-static-from)]";
	}
}

/** Subtle rim around buttons + center (inside highlight/beam when present). */
function trackBorderClass(colorMode: QuantitySpinnerColorMode): string {
	switch (colorMode) {
		case "light":
			return "ring-1 ring-inset ring-black/[0.08]";
		case "dark":
			return "ring-1 ring-inset ring-white/15";
		case "system":
			return "ring-1 ring-inset ring-black/[0.08] dark:ring-white/15";
	}
}

function inputTextClass(colorMode: QuantitySpinnerColorMode): string {
	switch (colorMode) {
		case "light":
			return "text-zinc-900";
		case "dark":
			return "text-white";
		case "system":
			return "text-zinc-900 dark:text-white";
	}
}

function inputSubtitleClass(colorMode: QuantitySpinnerColorMode): string {
	switch (colorMode) {
		case "light":
			return "text-zinc-400";
		case "dark":
			return "text-white/45";
		case "system":
			return "text-zinc-400 dark:text-white/45";
	}
}


function spinnerButtonSurfaceClass(
	variant: QuantitySpinnerButtonVariant,
): string {
	if (variant === "secondary") {
		// Mirrors SecondaryCta surface (packages/ui cannot import apps/web CTAs).
		return cn(
			"bg-gradient-to-b from-white via-neutral-50 to-neutral-100 text-neutral-900",
			"dark:from-[#243652] dark:via-[#1a2435] dark:to-[#0f1419] dark:text-white",
			"shadow-[0_1px_2px_rgb(15_23_42/0.08),0_3px_10px_rgb(15_23_42/0.06)]",
			"ring-1 ring-inset ring-white/50 dark:ring-white/12",
		);
	}
	return cn(
		SQ_PRIMARY_CTA_GRADIENT_CLASS,
		"text-amber-950",
		"shadow-md ring-1 ring-inset ring-white/10",
	);
}

function SpinnerEdgeButton({
	edge,
	onClick,
	disabled,
	variant,
	size,
	"aria-label": ariaLabel,
	children,
}: {
	edge: "left" | "right";
	onClick: () => void;
	disabled: boolean;
	variant: QuantitySpinnerButtonVariant;
	size: QuantitySpinnerSize;
	"aria-label": string;
	children: ReactNode;
}) {
	const compact = size === "compact";
	return (
		<RippleButton
			type="button"
			appearance="plain"
			variant="ghost"
			disabled={disabled}
			whileHover={false}
			whileTap={disabled ? false : { scale: 0.97 }}
			className={cn(
				"relative shrink-0 overflow-hidden p-0 font-black",
				"origin-center will-change-transform",
				compact
					? "size-8 h-8 min-h-8 w-8 min-w-8"
					: "size-11 h-11 min-h-11 w-11 min-w-11",
				spinnerButtonSurfaceClass(variant),
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40",
				disabled && "cursor-not-allowed opacity-30",
				edge === "left" && SPINNER_INNER_L,
				edge === "right" && SPINNER_INNER_R,
			)}
			onClick={onClick}
			aria-label={ariaLabel}
		>
			<span className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
				{children}
			</span>
		</RippleButton>
	);
}

function QuantitySpinnerBadge({ label }: { label: string }) {
	return (
		<span
			className="inline-flex w-max shrink-0 self-center rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase leading-tight tracking-wide text-white shadow-sm [text-shadow:0_1px_1px_rgba(0,0,0,0.45)] sm:text-[9px]"
			style={{
				background: `linear-gradient(135deg, ${SQ_AMBER_PAIR.from} 0%, ${SQ_AMBER_PAIR.to} 100%)`,
			}}
			aria-hidden
		>
			{label}
		</span>
	);
}

/** Matches completion overlay center copy (`font-black` / uppercase / tracking). */
const INPUT_VALUE_TYPE_CLASS =
	"text-center text-[11px] font-black uppercase tracking-wide sm:text-xs";

/**
 * Gold ± quantity control used by learn completion overlays and marketing pricing.
 */
export function QuantitySpinner({
	decreaseAriaLabel,
	increaseAriaLabel,
	onDecrease,
	onIncrease,
	decreaseDisabled = false,
	increaseDisabled = false,
	increaseDisabledTooltip = null,
	colorMode = "system",
	centerMode = "display",
	buttonVariant = "primary",
	size = "default",
	highlight = false,
	badge,
	className,
	centerClassName,
	children,
	inputId,
	inputValue,
	onInputChange,
	onInputFocus,
	onInputBlur,
	onInputKeyDown,
	onInputMouseDown,
	inputReadOnly = false,
	inputDisabled = false,
	inputAriaValueMin,
	inputAriaValueMax,
	inputAriaValueNow,
	inputAriaValueText,
	inputAriaInvalid,
	inputAriaDescribedBy,
	inputClassName,
	inputSubtitle,
}: QuantitySpinnerProps) {
	const badgeLabel =
		highlight === false
			? null
			: badge === false
				? null
				: (badge ?? DEFAULT_BADGE_LABEL);
	const compact = size === "compact";
	const iconClass = compact ? "size-3.5 shrink-0" : "size-4 shrink-0";

	const spinner = (
		<div
			className={cn(
				"relative flex w-full min-w-0 items-stretch",
				compact ? "min-h-9" : "min-h-[3rem]",
				TILE_RADIUS,
				trackSurfaceClass(colorMode),
				trackBorderClass(colorMode),
				"p-0.5",
				!highlight && className,
			)}
			data-quantity-spinner-size={size}
		>
			<SpinnerEdgeButton
				edge="left"
				variant={buttonVariant}
				size={size}
				aria-label={decreaseAriaLabel}
				disabled={decreaseDisabled}
				onClick={onDecrease}
			>
				<Minus className={iconClass} strokeWidth={3} />
			</SpinnerEdgeButton>

			<div
				className={cn(
					"flex min-w-0 flex-1 items-stretch",
					centerMode === "display" &&
						"*:flex *:min-w-0 *:flex-1",
					centerClassName,
				)}
			>
				{centerMode === "input" ? (
					<div
						className={cn(
							"flex min-w-0 flex-1 items-center gap-1.5",
							compact ? "px-1.5" : "px-2 sm:px-2.5",
						)}
					>
						{badgeLabel != null ? (
							<QuantitySpinnerBadge label={badgeLabel} />
						) : null}
						<div className="flex min-w-0 flex-1 flex-col items-stretch justify-center gap-0 py-0.5">
							<Input
								id={inputId}
								type="text"
								inputMode={
									inputReadOnly ? undefined : "numeric"
								}
								readOnly={inputReadOnly}
								disabled={inputDisabled}
								className={cn(
									"h-auto min-h-0 min-w-0 w-full rounded-none border-0 bg-transparent px-0 py-0 shadow-none",
									"tabular-nums leading-none focus-visible:ring-0 focus-visible:ring-offset-0",
									INPUT_VALUE_TYPE_CLASS,
									inputTextClass(colorMode),
									inputReadOnly && "cursor-text",
									inputClassName,
								)}
								value={inputValue ?? ""}
								aria-valuemin={inputAriaValueMin}
								aria-valuemax={inputAriaValueMax}
								aria-valuenow={inputAriaValueNow}
								aria-valuetext={inputAriaValueText}
								aria-invalid={
									inputAriaInvalid ? true : undefined
								}
								aria-describedby={inputAriaDescribedBy}
								onChange={(event) => {
									onInputChange?.(event.target.value);
								}}
								onFocus={onInputFocus}
								onBlur={onInputBlur}
								onKeyDown={onInputKeyDown}
								onMouseDown={onInputMouseDown}
							/>
							{inputSubtitle != null &&
							inputSubtitle.trim() !== "" ? (
								<span
									className={cn(
										"truncate text-center text-[9px] font-medium leading-none tracking-wide sm:text-[10px]",
										inputSubtitleClass(colorMode),
									)}
									aria-hidden
								>
									{inputSubtitle}
								</span>
							) : null}
						</div>
					</div>
				) : (
					children
				)}
			</div>

			{increaseDisabled && increaseDisabledTooltip ? (
								<ContentTooltip
					{...CONTENT_TOOLTIP_SHORT}
					side="top"
					contentClassName="max-w-[16rem]"
					content={increaseDisabledTooltip}
				>
					<span className="inline-flex shrink-0">
												<SpinnerEdgeButton
													edge="right"
													variant={buttonVariant}
													size={size}
													aria-label={increaseAriaLabel}
													disabled={increaseDisabled}
													onClick={onIncrease}
												>
													<Plus className={iconClass} strokeWidth={3} />
												</SpinnerEdgeButton>
											</span>
				</ContentTooltip>
			) : (
				<SpinnerEdgeButton
					edge="right"
					variant={buttonVariant}
					size={size}
					aria-label={increaseAriaLabel}
					disabled={increaseDisabled}
					onClick={onIncrease}
				>
					<Plus className={iconClass} strokeWidth={3} />
				</SpinnerEdgeButton>
			)}
		</div>
	);

	if (!highlight) {
		return spinner;
	}

	const innerRadiusPx = Math.max(0, 16 - HIGHLIGHT_BORDER_PX);

	return (
		<div
			className={cn("relative w-full min-w-0", className)}
			style={{
				padding: HIGHLIGHT_BORDER_PX,
				borderRadius: 16,
				background: `linear-gradient(135deg, ${SQ_AMBER_PAIR.from} 0%, ${SQ_AMBER_PAIR.to} 100%)`,
			}}
		>
			<div
				className="min-w-0 overflow-hidden"
				style={{ borderRadius: innerRadiusPx }}
			>
				{spinner}
			</div>
		</div>
	);
}
